import { useDispatch } from "react-redux";

import { ReduxAxiosApiResponse } from "src/types";

type ReduxAxiosAction = {
  payload: { request: unknown };
};

type PromiseDispatch = <T>(
  action: ReduxAxiosAction,
) => Promise<ReduxAxiosApiResponse<T>>;

const isSuccess = (result: ReduxAxiosApiResponse<unknown>): boolean => {
  return result.type.includes("_SUCCESS");
};

/**
 * usePromiseDispatch
 *
 * A React hook that wraps Redux dispatch to return a Promise when dispatching actions
 * that go through axios-redux-middleware. This enables async/await syntax and proper
 * error handling for API calls.
 *
 * The hook automatically checks the Redux action type and resolves the promise for
 * successful actions (action types containing "_SUCCESS") or rejects it for failed actions.
 *
 * @returns {PromiseDispatch} A dispatch function that returns a Promise with the API response
 *
 * @example
 * ```tsx
 * const promiseDispatch = usePromiseDispatch();
 *
 * try {
 *   const result = await promiseDispatch(fetchUserAction);
 *   console.log('Success:', result.payload.data);
 * } catch (error) {
 *   console.error('API Error:', error);
 * }
 * ```
 */
export const usePromiseDispatch = (): PromiseDispatch => {
  const dispatch = useDispatch();

  return <T>(action: ReduxAxiosAction): Promise<ReduxAxiosApiResponse<T>> =>
    (dispatch(action) as unknown as Promise<ReduxAxiosApiResponse<T>>).then(
      (result) => {
        if (isSuccess(result)) return result;
        return Promise.reject(result);
      },
    );
};
